/**
 * The CCTV links tab (§12.1, SEC-4).
 *
 * A partner in Dhaka opening the shed camera from their phone is the whole
 * feature. Everything else here is about not turning that into a way to attack
 * them.
 *
 * Two defences, and the second exists because the first can be bypassed. The
 * server allowlists the URL scheme (SEC-4), so `javascript:` never reaches the
 * database. This screen checks again before rendering an anchor, because a row
 * written before that validator existed — or through some future path that
 * skips it — would otherwise become stored XSS on a screen every partner
 * opens.
 *
 * `rel="noopener noreferrer"` on every outbound link: without `noopener` the
 * opened page gets a handle on this one through `window.opener` and can
 * navigate it to a copy of the login screen.
 */

import { useTranslation } from '../../i18n/useTranslation.js';
import { useSettings, type CctvLink } from '../finance/api.js';

/** The schemes the server allows, checked again here (defence in depth). */
const SAFE_SCHEMES = ['http:', 'https:', 'rtsp:', 'rtsps:'];

export function isRenderableLink(url: string): boolean {
  try {
    return SAFE_SCHEMES.includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

/**
 * rtsp:// will not open in a browser, so those are shown as copyable text
 * rather than a link that silently does nothing when tapped.
 */
const isBrowserOpenable = (url: string): boolean => url.startsWith('http:') || url.startsWith('https:');

export function CctvLinks(): JSX.Element {
  const { t } = useTranslation();
  const { data: settings, isLoading, isError, refetch } = useSettings();

  const links: CctvLink[] = Array.isArray(settings?.cctvLinks) ? (settings.cctvLinks as CctvLink[]) : [];
  const safe = links.filter((link) => typeof link?.url === 'string' && isRenderableLink(link.url));

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('cctv.title')}</h1>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {settings !== undefined && safe.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">{t('cctv.empty')}</p>
      )}

      <ul className="space-y-2">
        {safe.map((link) => (
          <li key={`${link.name}-${link.url}`} className="rounded-xl bg-white px-4 py-3 shadow-sm">
            <p className="text-sm font-medium text-gray-900">{link.name}</p>

            {isBrowserOpenable(link.url) ? (
              <a
                href={link.url}
                target="_blank"
                // noopener: without it the opened page can navigate this one
                // to a copy of the login screen through window.opener.
                rel="noopener noreferrer"
                className="mt-1 inline-block min-h-touch break-all text-sm text-green-700 underline"
              >
                {t('cctv.open')}
              </a>
            ) : (
              <p className="mt-1 break-all text-xs text-gray-500">{link.url}</p>
            )}
          </li>
        ))}
      </ul>

      <p className="mt-4 text-xs text-gray-400">{t('cctv.adminOnly')}</p>
    </div>
  );
}
